import * as React from 'react';
import { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper } from '@mui/material';
import { useAuth } from '../../authentication/AuthContext';
import axiosInstance from '../../authentication/AuthContext';
import { StyledDataGrid } from '../customizations/dataGrid';

export default function CustomizedDataGrid() {
    const { accessToken } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Document columns
    const columns = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'title', headerName: 'Title', flex: 1, minWidth: 200 },
        { field: 'filename', headerName: 'Filename', flex: 1, minWidth: 150 },
        {
            field: 'file_size',
            headerName: 'Size',
            width: 100,
            renderCell: (params) => {
                const bytes = params.value;
                if (bytes === 0) return '0 B';
                const k = 1024;
                const sizes = ['B', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return (
                    parseFloat((bytes / Math.pow(k, i)).toFixed(1)) +
                    ' ' +
                    sizes[i]
                );
            },
        },
        { field: 'file_type', headerName: 'Type', width: 120 },
        {
            field: 'uploaded_at',
            headerName: 'Uploaded',
            width: 150,
            renderCell: (params) => {
                if (!params.value) return '-';
                return new Date(params.value).toLocaleDateString();
            },
        },
        {
            field: 'uploaded_by',
            headerName: 'Uploaded By',
            width: 120,
            renderCell: (params) => {
                const user = params.value;
                return user ? user.username : '-';
            },
        },
    ];

    // Fetch documents from API
    useEffect(() => {
        const fetchDocuments = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get('/documents/list/');

                // Handle different response structures
                let documentsData = response.data;
                if (response.data && response.data.documents) {
                    documentsData = response.data.documents;
                } else if (response.data && response.data.results) {
                    documentsData = response.data.results;
                } else if (response.data && response.data.data) {
                    documentsData = response.data.data;
                }

                setDocuments(documentsData || []);
            } catch (err) {
                console.error('Failed to fetch documents:', err);
                setError('Failed to load documents');
            } finally {
                setLoading(false);
            }
        };

        if (accessToken) {
            fetchDocuments();
        }
    }, [accessToken]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Paper variant="outlined" sx={{ elevation: 1, mb: 2 }}>
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Documents
                </Typography>
                <Typography variant="caption" gutterBottom>
                    {documents.length} Documents found
                </Typography>
                {documents.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                        No documents found
                    </Typography>
                ) : (
                    <StyledDataGrid
                        checkboxSelection
                        rows={documents}
                        columns={columns}
                        getRowId={(row) => row.id}
                        loading={loading}
                        disableRowSelectionOnClick
                        initialState={{
                            pagination: { paginationModel: { pageSize: 20 } },
                        }}
                        pageSizeOptions={[10, 20, 50]}
                        disableColumnResize
                        density="compact"
                        sx={{
                            '& .MuiDataGrid-row': {
                                backgroundColor: 'white',
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                            '& .MuiDataGrid-row.Mui-selected': {
                                backgroundColor: '#e3f2fd',
                            },
                            '& .MuiDataGrid-row.Mui-selected:hover': {
                                backgroundColor: '#bbdefb',
                            },
                        }}
                        slotProps={{
                            filterPanel: {
                                filterFormProps: {
                                    logicOperatorInputProps: {
                                        variant: 'outlined',
                                        size: 'small',
                                    },
                                    columnInputProps: {
                                        variant: 'outlined',
                                        size: 'small',
                                        sx: { mt: 'auto' },
                                    },
                                    operatorInputProps: {
                                        variant: 'outlined',
                                        size: 'small',
                                        sx: { mt: 'auto' },
                                    },
                                    valueInputProps: {
                                        InputComponentProps: {
                                            variant: 'outlined',
                                            size: 'small',
                                        },
                                    },
                                },
                            },
                        }}
                    />
                )}
            </Box>
        </Paper>
    );
}
